import React from 'react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import { Button, NoSsr } from '@mui/material';
import { css, keyframes, styled } from '@mui/material/styles';
import {
  Card,
  CardSection,
  IconPlayRoundFilled,
  IconVideoCamera,
} from '@maiia/design-system';
import { getCardFullName } from '@/components/utils';
import DeepLink from '@/components/atoms/DeepLink/DeepLink';
import Link from '../../atoms/Link/Link';
import { useMediaQueryBreakPoint, IS_PHONE_OR_TABLET } from '@/src/hooks';

import DialogNowTlc from './DialogNowTlc';
import { useTranslation } from '@/src/i18n';
import Text from '../../atoms/Text/Text';
import { analyticsEvent } from '@/src/analytic_tag_manager';
import { APP_ROUTES } from '@/src/constants';

/* JS styling */

const BlockTlcWrapper = styled(Card, {
  shouldForwardProp: () => true,
})(
  ({ theme }) => css`
    .card-section-icon,
    .card-section-title {
      color: ${theme.palette.text.link};
    }
  `,
);

const ButtonWrapper = styled('div', { shouldForwardProp: () => true })(
  ({ theme }) => css`
    max-width: ${theme.spacing(50)};
    margin: ${theme.spacing(2)} auto 0 auto;
    overflow: hidden;
  `,
);

const mirrorEffetAnimation = keyframes`
  0% {
    left: 0;
  }
  15% {
    left: 100%;
  }
  20%, 100% {
    left: 125%;
  }
`;

const ButtonWithMirrorEffet = styled(Button, { shouldForwardProp: () => true })(
  ({ theme }) => css`
    position: relative;
    overflow: hidden; // Make sure mirror effect won't be visible outside the button

    &:after {
      content: '';
      position: absolute;
      width: 25%;
      height: 100%;
      top: 0;
      left: -25%;
      background: ${theme.palette.background.primaryVeryTransparent};
      transform: skewX(-15deg);
      animation: ${mirrorEffetAnimation} 4s linear infinite;
    }
  `,
);

/* Component */

const gaDirectTlcEvent = () =>
  analyticsEvent({
    category: 'PractitionerOrCenterPage',
    action: 'ClickCTADirectTLC',
  });

const BlockTlc = ({ card }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const isPhoneOrTablet = useMediaQueryBreakPoint(IS_PHONE_OR_TABLET);
  const [openTlcModal, setOpenTlcModal] = React.useState(false);
  const {
    settings: {
      teleconsultationAvailabilityStartTime,
      teleconsultationAvailabilityEndTime,
    },
  } = card;

  const isEnabledTlc =
    teleconsultationAvailabilityStartTime &&
    teleconsultationAvailabilityEndTime &&
    dayjs().isBetween(
      teleconsultationAvailabilityStartTime,
      teleconsultationAvailabilityEndTime,
    );

  const substitute = card?.substituteData;
  const fullName = getCardFullName(t, card);

  return (
    <BlockTlcWrapper hasOldStyle data-testid="teleconsultation-section">
      <CardSection icon={<IconVideoCamera />} title={t('title_tlc')}>
        <p>
          {`${t('tlc-heading-text')} `}
          <Link href={APP_ROUTES.TLC.BASE_URL} passHref legacyBehavior>
            <a
              title="En savoir plus"
              className="link-info"
              target="_blank"
              onClick={() => {
                analyticsEvent({
                  category: 'PractitionerOrCenterPage',
                  action: 'ClickMoreInfoTLC',
                });
              }}
            >
              {t('know_more_about_tlc')}
            </a>
          </Link>
        </p>
        {router.query.fromImmediateTlc && Boolean(substitute) && (
          <Text component="p" variant="paragraph">
            {t('substitute-for', {
              fullName,
            })}
          </Text>
        )}
        <NoSsr>
          {isEnabledTlc &&
            (!isPhoneOrTablet ? (
              <ButtonWrapper>
                <ButtonWithMirrorEffet
                  color="secondary"
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setOpenTlcModal(true);
                    gaDirectTlcEvent();
                  }}
                  data-cy="btn-prat-tlc-online"
                  data-testid="btn-prat-tlc-online-desktop"
                  endIcon={<IconPlayRoundFilled />}
                >
                  {t(
                    isEmpty(substitute)
                      ? 'no-rdv-tlc-with-prat'
                      : 'no-rdv-tlc-with-substitute',
                  )}
                </ButtonWithMirrorEffet>
                <DialogNowTlc
                  card={card}
                  setOpen={setOpenTlcModal}
                  open={openTlcModal}
                />
              </ButtonWrapper>
            ) : (
              <ButtonWrapper>
                <DeepLink
                  href={{
                    pathname: '/tlc/[center-id]/[practitioner-id]',
                    query: {
                      'center-id': card.center?.id,
                      'practitioner-id': card.professional?.practitionerId,
                      url: card.url,
                    },
                  }}
                  passHref
                >
                  <ButtonWithMirrorEffet
                    size="small"
                    color="secondary"
                    variant="contained"
                    onClick={gaDirectTlcEvent}
                    data-cy="btn-prat-tlc-online"
                    data-testid="btn-prat-tlc-online-tablet"
                    endIcon={<IconPlayRoundFilled />}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('no-rdv-tlc-with-prat')}
                  </ButtonWithMirrorEffet>
                </DeepLink>
              </ButtonWrapper>
            ))}
        </NoSsr>
      </CardSection>
    </BlockTlcWrapper>
  );
};

export default BlockTlc;
